/**
 * Keystroke-lag tracker for the Notes (TipTap) editor.
 *
 * Why this module exists
 * ----------------------
 * The "letters lag behind typing" complaint that triggered IS-12249/IS-12251
 * was diagnosed historically by anecdote and by the manual `PerfOverlay`. We
 * have no production telemetry that proves the bug is gone — only INP from
 * Web Vitals, which:
 *   - is bucketed (p75/p95/p99 only via the Vitals event)
 *   - is page-level, not editor-level
 *   - cannot be pivoted by "how many charts has this user opened in this
 *     session", which is the failure mode confirmed in
 *     tmp/test-evidence/rum-typing-lag-7day-2026-04-23.md (sessions with 3+
 *     `patient.chart.switch` events have 70× worse keyboard INP than 0–2
 *     switch sessions).
 *
 * This tracker fills that gap by emitting a `note.keystroke_lag` RUM event
 * on every "noticeable" keystroke that hits a `.ProseMirror` element,
 * tagged with session-level counters. We can then run a Logs Insights query
 * grouping by `chartSwitchesInSession` to confirm the production fix.
 *
 * Cost / safety
 * -------------
 *   - PerformanceObserver(type:'event') is a browser-native callback; no
 *     polling, no fetch patching, no MutationObserver. Zero cost when the
 *     user is not interacting with an editor.
 *   - durationThreshold: 40ms means the browser only reports events that
 *     already exceed half a frame at 60Hz — these are the ones a human can
 *     perceive. Sub-threshold keystrokes are dropped at the browser layer
 *     and never reach JS.
 *   - Per-session throttle: we cap emissions at one event every
 *     EMIT_INTERVAL_MS milliseconds. Even a frantic typist generates ~10
 *     events/min, well inside RUM ingest limits.
 *   - Payload is a closed allowlist of timings + counts + pathname. No PHI.
 */

import { trackNoteKeystrokeLag } from './rum-events';

const EMIT_INTERVAL_MS = 1000;
const DURATION_THRESHOLD_MS = 40;

// Tracker state lives at module scope so a second `init()` call (e.g. React
// Fast Refresh) is a no-op rather than installing a second observer.
type TrackerState = {
  inited: boolean;
  observer: PerformanceObserver | null;
  chartSwitchesInSession: number;
  lastPatientKey: string | null;
  lastEmitTs: number;
  // First-sight timestamps for editor elements, so we can compute mount
  // age without requiring a code change in NoteEditor.tsx.
  editorFirstSeen: WeakMap<Element, number>;
  // Captured at init() time from RumProvider; used to attach tenant/practice
  // context to every event so the data is queryable per-org without joining.
  baseAttrs: { tenantId?: string; practiceId?: string } | null;
};

const state: TrackerState = {
  inited: false,
  observer: null,
  chartSwitchesInSession: 0,
  lastPatientKey: null,
  lastEmitTs: 0,
  editorFirstSeen: new WeakMap<Element, number>(),
  baseAttrs: null,
};

/** Pull `patientId` (or `sax_id`) from the current URL, as the chart key. */
function currentPatientKey(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const u = new URL(window.location.href);
    const patientId = u.searchParams.get('patientId') || u.searchParams.get('sax_id');
    if (patientId) return patientId;
    // Fall back to a path segment after `/patients/` if any future routing
    // moves the chart id out of the query string.
    const m = u.pathname.match(/\/patients\/(\d+)/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/**
 * Increment chartSwitchesInSession when the chart key in the URL changes.
 * We hook into the History API by wrapping pushState/replaceState (browsers
 * do not emit any event on programmatic navigation otherwise), and we also
 * listen to popstate. The first observed value is the baseline (count = 0).
 */
function installChartSwitchCounter(): void {
  if (typeof window === 'undefined') return;
  state.lastPatientKey = currentPatientKey();

  const tick = () => {
    const key = currentPatientKey();
    if (key !== state.lastPatientKey) {
      // Only count transitions where we now have a key; null→null and
      // X→null (navigated off /patients) are not chart switches.
      if (key !== null && state.lastPatientKey !== null) {
        state.chartSwitchesInSession += 1;
      }
      state.lastPatientKey = key;
    }
  };

  window.addEventListener('popstate', tick);

  // Wrap pushState/replaceState exactly once per page load. Tag the wrapped
  // copy so subsequent inits don't double-wrap (would cause double counting).
  const w = window as unknown as Window & { __kslPatchedHistory?: boolean };
  if (!w.__kslPatchedHistory) {
    w.__kslPatchedHistory = true;
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    history.pushState = function (...args: Parameters<typeof origPush>) {
      const r = origPush(...args);
      // Schedule on a microtask so the URL is updated before we read it.
      Promise.resolve().then(tick);
      return r;
    };
    history.replaceState = function (...args: Parameters<typeof origReplace>) {
      const r = origReplace(...args);
      Promise.resolve().then(tick);
      return r;
    };
  }
}

// `.ProseMirror` is the stable, framework-applied class on the contenteditable
// root for any TipTap/ProseMirror editor instance. Our NoteEditor.tsx does not
// add a custom wrapper class (verified 2026-04-23), so .ProseMirror is the
// only reliable hook. If you ever rename or wrap it, update this selector and
// the matching one in scripts/typing-lag-stress.mjs together.
const EDITOR_SELECTOR = '.ProseMirror';

function editorAncestor(target: EventTarget | null): Element | null {
  if (!target || !(target instanceof Element)) return null;
  return target.closest(EDITOR_SELECTOR) as Element | null;
}

/** Snapshot of the JS heap usage in MB, when the browser exposes it. */
function readHeapMb(): number | null {
  // performance.memory is a Chrome-only non-standard API; gate access.
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number };
  };
  if (!perf.memory || typeof perf.memory.usedJSHeapSize !== 'number') {
    return null;
  }
  return Math.round(perf.memory.usedJSHeapSize / (1024 * 1024));
}

/**
 * Initialise the tracker. Idempotent. Safe to call before/after RUM init —
 * if RUM is not yet ready, `recordRumEvent` will warn and drop the sample;
 * once RUM comes online subsequent samples land normally.
 */
export function initKeystrokeLagTracker(opts?: {
  tenantId?: string;
  practiceId?: string;
}): void {
  if (typeof window === 'undefined') return;
  if (state.inited) return;
  // PerformanceObserver type 'event' is required (Chromium 89+, Safari 16+).
  // Older browsers silently get no telemetry rather than throwing.
  if (typeof PerformanceObserver === 'undefined') return;
  const supported = (PerformanceObserver as unknown as {
    supportedEntryTypes?: readonly string[];
  }).supportedEntryTypes;
  if (supported && !supported.includes('event')) return;

  state.inited = true;
  state.baseAttrs = {
    tenantId: opts?.tenantId,
    practiceId: opts?.practiceId,
  };

  installChartSwitchCounter();

  const obs = new PerformanceObserver((list) => {
    // PerformanceEventTiming is the concrete type for entries from
    // type:'event'. The cast keeps us off `any` while still tolerating
    // older lib.dom.d.ts versions.
    const entries = list.getEntries() as Array<
      PerformanceEntry & { target?: Element | null; processingStart?: number; processingEnd?: number }
    >;
    for (const entry of entries) {
      // Filter to keyboard-driven interactions only. A mouse click on a
      // toolbar button can also exceed the threshold but is not "typing
      // lag" per the original complaint.
      const name = entry.name;
      if (
        name !== 'input' &&
        name !== 'beforeinput' &&
        name !== 'keydown' &&
        name !== 'keypress' &&
        name !== 'keyup'
      ) {
        continue;
      }

      const editorEl = editorAncestor(entry.target ?? null);
      if (!editorEl) continue;

      // Compute or remember when this editor first surfaced, to estimate
      // mount age — older mounts are more likely to be victims of
      // accumulated state.
      let firstSeen = state.editorFirstSeen.get(editorEl);
      if (firstSeen === undefined) {
        firstSeen = performance.now();
        state.editorFirstSeen.set(editorEl, firstSeen);
      }

      const now = performance.now();
      // Throttle: at most one emission per EMIT_INTERVAL_MS. We always
      // pick the FIRST eligible entry of the batch, not the worst, to
      // avoid biasing toward outliers. The browser reports the longest
      // entries within an observation window anyway.
      if (now - state.lastEmitTs < EMIT_INTERVAL_MS) continue;
      state.lastEmitTs = now;

                  const editorInstanceCount = document.querySelectorAll(EDITOR_SELECTOR).length;
      const tippyCount = document.querySelectorAll('[data-tippy-root]').length;
      // querySelectorAll('*') walks the whole DOM. For a chart with notes
      // open this is typically <10k nodes, costing well under 1ms. Worth
      // the visibility into accumulating DOM bloat.
      const domNodeCount = document.querySelectorAll('*').length;

      trackNoteKeystrokeLag({
        chartSwitchesInSession: state.chartSwitchesInSession,
        domNodeCount,
        editorInstanceCount,
        editorMountAgeMs: Math.round(now - firstSeen),
        heapMb: readHeapMb(),
        interactionType: name,
        pageId: window.location.pathname,
        practiceId: state.baseAttrs?.practiceId,
        tenantId: state.baseAttrs?.tenantId,
        tippyCount,
        valueMs: Math.round(entry.duration),
      });
    }
  });

  try {
    obs.observe({
      type: 'event',
      // The `durationThreshold` is the documented lever for cutting down on
      // event entry noise. Browsers spec a minimum of 16ms; we ask for 40ms
      // to focus on perceptible lag. Older Safari may not honour this and
      // will fall back to its own minimum, which is fine.
      durationThreshold: DURATION_THRESHOLD_MS,
      buffered: true,
    } as PerformanceObserverInit);
  } catch (err) {
    // Disable cleanly if the browser rejects our options (e.g. missing
    // durationThreshold support). We'd rather have no data than a crash on
    // the user's first keystroke.
    console.warn('[keystroke-lag-tracker] observer.observe failed; disabling', err);
    state.inited = false;
    return;
  }

  state.observer = obs;
}

/** Refresh the tenant/practice context, e.g. after the user switches roles. */
export function updateKeystrokeLagBaseAttrs(opts: {
  tenantId?: string;
  practiceId?: string;
}): void {
  state.baseAttrs = {
    tenantId: opts.tenantId,
    practiceId: opts.practiceId,
  };
}

/** Test/debug accessor. Not used in production code paths. */
export function _debugKeystrokeLagState(): Readonly<TrackerState> {
  return state;
}
